<template>
  <v-app>
    <v-app-bar
      app
      flat
      color="grey lighten-5"
      height="64"
      class="app-header"
      elevation="0"
    >
      <v-app-bar-nav-icon
        @click="drawer = !drawer"
        color="grey darken-2"
        class="d-lg-none"
      ></v-app-bar-nav-icon>

      <v-toolbar-title class="d-flex align-center ml-2">
        <v-avatar size="28" color="grey darken-3" class="mr-3">
          <v-icon color="white" size="16">mdi-headset</v-icon>
        </v-avatar>
        <span class="text-h6 font-weight-light grey--text text--darken-3">
          Support Center
        </span>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- Search Bar -->
      <v-text-field
        v-model="globalSearch"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search tickets, users, or content..."
        outlined
        dense
        hide-details
        single-line
        color="grey darken-2"
        class="search-input mx-4 d-none d-sm-flex"
        style="max-width: 400px"
        background-color="white"
      ></v-text-field>

      <!-- Notifications -->
      <v-btn
        icon
        color="grey darken-2"
        class="mr-2"
        @click="showNotifications = !showNotifications"
      >
        <v-badge
          :content="notificationCount"
          :value="notificationCount > 0"
          color="red"
          overlap
          dot
        >
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>

      <!-- AI Chat Toggle -->
      <v-btn
        icon
        color="grey darken-2"
        class="mr-2"
        @click="toggleAIChat"
        :class="{ 'primary--text': aiChatDrawer }"
      >
        <v-icon>mdi-robot-outline</v-icon>
      </v-btn>

      <!-- User Menu -->
      <v-menu
        offset-y
        origin="top right"
        transition="slide-y-transition"
        v-if="isAuthenticated"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            color="grey darken-2"
            v-bind="attrs"
            v-on="on"
            class="user-menu-btn"
          >
            <v-avatar size="32" color="grey lighten-2" class="mr-2">
              <span class="text-caption grey--text text--darken-2">
                {{ getUserInitials(currentUser.name) }}
              </span>
            </v-avatar>
            <span class="d-none d-md-inline">{{ currentUser.name }}</span>
            <v-icon right size="18">mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-card elevation="8" class="user-menu">
          <v-list dense>
            <v-list-item>
              <v-list-item-avatar>
                <v-avatar color="grey lighten-2">
                  <span class="text-caption">{{
                    getUserInitials(currentUser.name)
                  }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ currentUser.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ currentUser.email }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-divider class="my-2"></v-divider>

            <v-list-item @click="showProfile = true">
              <v-list-item-icon>
                <v-icon size="20" color="grey darken-1">mdi-account-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body-2">Profile</v-list-item-title>
            </v-list-item>

            <v-list-item @click="showSettings = true">
              <v-list-item-icon>
                <v-icon size="20" color="grey darken-1">mdi-cog-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body-2">Settings</v-list-item-title>
            </v-list-item>

            <v-divider class="my-2"></v-divider>

            <v-list-item @click="handleLogout">
              <v-list-item-icon>
                <v-icon size="20" color="grey darken-1">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body-2">Sign Out</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- Auth Required State -->
    <v-main>
      <v-container fill-height>
        <v-row align="center" justify="center" v-if="!isAuthenticated">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            <v-card elevation="0" class="auth-card pa-8" color="white">
              <!-- Logo & Title -->
              <div class="text-center mb-8">
                <v-avatar size="72" color="grey darken-3" class="mb-6">
                  <v-icon color="white" size="32">mdi-headset</v-icon>
                </v-avatar>
                <h1 class="text-h4 font-weight-light grey--text text--darken-3 mb-2">
                  Support Center
                </h1>
                <p class="text-body-2 grey--text text--darken-1">
                  Sign in to access your support dashboard
                </p>
              </div>

              <!-- Login Form -->
              <v-form
                v-if="!showRegister"
                ref="loginForm"
                v-model="loginValid"
                lazy-validation
              >
                <v-text-field
                  v-model="email"
                  label="Email"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required, rules.email]"
                  prepend-inner-icon="mdi-email-outline"
                  class="mb-4"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  label="Password"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required, rules.password]"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append="showPassword = !showPassword"
                  class="mb-6"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-btn
                  block
                  large
                  depressed
                  color="grey darken-3"
                  dark
                  :loading="loading"
                  :disabled="!loginValid"
                  @click="handleLogin"
                  class="mb-4 text-none"
                >
                  Sign In
                </v-btn>

                <div class="text-center">
                  <v-btn
                    text
                    small
                    color="grey darken-2"
                    @click="showRegister = true"
                    class="text-none"
                  >
                    Don't have an account? Sign up
                  </v-btn>
                </div>
              </v-form>

              <!-- Register Form -->
              <v-form v-else ref="registerForm" v-model="registerValid" lazy-validation>
                <v-text-field
                  v-model="name"
                  label="Full Name"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-account-outline"
                  class="mb-4"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-text-field
                  v-model="registerEmail"
                  label="Email"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required, rules.email]"
                  prepend-inner-icon="mdi-email-outline"
                  class="mb-4"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-text-field
                  v-model="registerPassword"
                  label="Password"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required, rules.password]"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append="showPassword = !showPassword"
                  class="mb-4"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-text-field
                  v-model="confirmPassword"
                  label="Confirm Password"
                  outlined
                  dense
                  color="grey darken-3"
                  :rules="[rules.required, rules.passwordMatch]"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-outline"
                  class="mb-6"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-btn
                  block
                  large
                  depressed
                  color="grey darken-3"
                  dark
                  :loading="loading"
                  :disabled="!registerValid"
                  @click="handleRegister"
                  class="mb-4 text-none"
                >
                  Create Account
                </v-btn>

                <div class="text-center">
                  <v-btn
                    text
                    small
                    color="grey darken-2"
                    @click="showRegister = false"
                    class="text-none"
                  >
                    Already have an account? Sign in
                  </v-btn>
                </div>
              </v-form>
            </v-card>
          </v-col>
        </v-row>

        <!-- Main App Layout -->
        <div v-else>
          <!-- Navigation Drawer -->

          <!-- Tickets View -->
          <TicketsView v-if="selectedView === 'tickets'" :api-url="apiUrl" />
        </div>
      </v-container>
    </v-main>
    <!-- AI Chat Component -->
    <AIChat
      v-model="aiChatDrawer"
      :api-url="apiUrl"
      :current-user="currentUser"
      :is-authenticated="isAuthenticated"
      @toggle="toggleAIChat"
    />

    <!-- Create Ticket Dialog -->
    <v-dialog v-model="showCreateTicketDialog" max-width="700" persistent scrollable>
      <v-card elevation="8" color="white">
        <v-card-title class="pa-6 grey lighten-5">
          <span class="text-h5 font-weight-regular grey--text text--darken-3">
            Create New Ticket
          </span>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="createTicketForm" lazy-validation>
            <v-text-field
              v-model="newTicket.title"
              label="Subject *"
              outlined
              color="grey darken-3"
              :rules="[rules.required]"
              background-color="grey lighten-5"
              class="mb-4"
            ></v-text-field>

            <v-row class="mb-4">
              <v-col cols="12" sm="6">
                <v-select
                  v-model="newTicket.category_id"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  label="Category *"
                  outlined
                  color="grey darken-3"
                  :rules="[rules.required]"
                  background-color="grey lighten-5"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="newTicket.priority"
                  :items="priorities"
                  label="Priority"
                  outlined
                  color="grey darken-3"
                  background-color="grey lighten-5"
                ></v-select>
              </v-col>
            </v-row>

            <v-textarea
              v-model="newTicket.description"
              label="Description *"
              outlined
              rows="5"
              color="grey darken-3"
              :rules="[rules.required]"
              background-color="grey lighten-5"
              class="mb-4"
            ></v-textarea>

            <v-file-input
              v-model="newTicket.attachments"
              label="Attachments"
              multiple
              outlined
              show-size
              chips
              color="grey darken-3"
              background-color="grey lighten-5"
              prepend-icon="mdi-paperclip"
            ></v-file-input>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-2"
            @click="closeCreateTicketDialog"
            class="text-none mr-2"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="grey darken-3"
            dark
            @click="createTicket"
            :loading="creatingTicket"
            class="text-none"
          >
            Create Ticket
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="showSuccess" color="green darken-1" timeout="4000" bottom left>
      <v-icon left color="white">mdi-check-circle</v-icon>
      {{ successMessage }}
    </v-snackbar>

    <v-snackbar v-model="showError" color="red darken-1" timeout="6000" bottom left>
      <v-icon left color="white">mdi-alert-circle</v-icon>
      {{ error }}
    </v-snackbar>
  </v-app>
</template>

<script>
import TicketsView from "./TicketsView.vue";
import AIChat from "./AIChat.vue";

export default {
  name: "AppLayout",
  components: {
    TicketsView,
    AIChat,
  },
  props: {
    apiUrl: {
      type: String,
      default: "https://ai-bos.co/mcp-tickets",
    },
  },
  data() {
    return {
      // UI State
      drawer: true,
      aiChatDrawer: false,
      selectedView: "tickets",
      selectedMenuItem: 0,
      globalSearch: "",
      notificationCount: 3,
      showNotifications: false,
      showProfile: false,
      showSettings: false,
      showCreateTicketDialog: false,

      // Form States
      loading: false,
      error: "",
      showError: false,
      successMessage: "",
      showSuccess: false,
      showRegister: false,
      loginValid: false,
      registerValid: false,
      showPassword: false,

      // Menu Items
      menuItems: [
        { title: "Dashboard", icon: "mdi-view-dashboard-outline", value: "dashboard" },
        { title: "Tickets", icon: "mdi-ticket-outline", value: "tickets", count: 12 },
        { title: "Analytics", icon: "mdi-chart-line-variant", value: "analytics" },
        { title: "Knowledge Base", icon: "mdi-book-open-outline", value: "knowledge" },
        { title: "Settings", icon: "mdi-cog-outline", value: "settings" },
      ],

      // Dashboard Data
      dashboardMetrics: [
        {
          title: "Open Tickets",
          value: "24",
          icon: "mdi-ticket-outline",
          color: "orange",
        },
        {
          title: "Resolved Today",
          value: "12",
          icon: "mdi-check-circle-outline",
          color: "green",
        },
        {
          title: "Avg Response",
          value: "2.5h",
          icon: "mdi-clock-outline",
          color: "blue",
        },
        { title: "Satisfaction", value: "94%", icon: "mdi-heart-outline", color: "pink" },
      ],

      recentActivity: [
        {
          icon: "mdi-ticket-outline",
          color: "green",
          text: "Ticket #1234 resolved",
          time: "2 min ago",
        },
        {
          icon: "mdi-comment-outline",
          color: "blue",
          text: "New comment on #1235",
          time: "5 min ago",
        },
        {
          icon: "mdi-account-plus-outline",
          color: "orange",
          text: "New user registered",
          time: "10 min ago",
        },
        {
          icon: "mdi-ticket-outline",
          color: "red",
          text: "High priority ticket created",
          time: "15 min ago",
        },
      ],

      // Validation Rules
      rules: {
        required: (v) => !!v || "This field is required",
        email: (v) => /.+@.+\..+/.test(v) || "Invalid email address",
        password: (v) => (v && v.length >= 6) || "Minimum 6 characters required",
        passwordMatch: (v) => v === this.registerPassword || "Passwords don't match",
      },

      // Auth Data
      isAuthenticated: false,
      currentUser: null,
      authToken: null,
      email: "",
      password: "",
      name: "",
      registerEmail: "",
      registerPassword: "",
      confirmPassword: "",

      // Create Ticket Data
      newTicket: {
        title: "",
        description: "",
        category_id: null,
        priority: "medium",
        attachments: [],
      },
      categories: [],
      priorities: [
        { text: "Low", value: "low" },
        { text: "Medium", value: "medium" },
        { text: "High", value: "high" },
        { text: "Critical", value: "critical" },
      ],
      creatingTicket: false,
    };
  },

  mounted() {
    this.checkAuth();

    // Listen for ticket creation events
    this.$root.$on("show-create-ticket", () => {
      this.showCreateTicketDialog = true;
    });
  },

  beforeDestroy() {
    this.$root.$off("show-create-ticket");
  },

  methods: {
    // UI Methods
    toggleAIChat() {
      this.aiChatDrawer = !this.aiChatDrawer;
    },

    // Auth Methods
    checkAuth() {
      const token = localStorage.getItem("support_token");
      const user = localStorage.getItem("support_user");

      if (token && user) {
        this.authToken = token;
        this.currentUser = JSON.parse(user);
        this.isAuthenticated = true;
        this.loadUserData();
      }
    },

    async handleLogin() {
      if (!this.$refs.loginForm.validate()) return;

      this.loading = true;
      try {
        const response = await fetch(`${this.apiUrl}/users/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.email,
            password: this.password,
          }),
        });

        const data = await response.json();
        if (response.ok && data.token) {
          this.authToken = data.token;
          this.currentUser = data.user;
          this.isAuthenticated = true;
          localStorage.setItem("support_token", data.token);
          localStorage.setItem("support_user", JSON.stringify(data.user));
          this.resetLoginForm();
          this.loadUserData();
          this.successMessage = `Welcome back, ${data.user.name}!`;
          this.showSuccess = true;
        } else {
          this.error = data.error || "Login failed";
          this.showError = true;
        }
      } catch (err) {
        this.error = "Connection error";
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    async handleRegister() {
      if (!this.$refs.registerForm.validate()) return;

      this.loading = true;
      try {
        const response = await fetch(`${this.apiUrl}/users/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: this.name,
            email: this.registerEmail,
            password: this.registerPassword,
            role: "client",
          }),
        });

        const data = await response.json();
        if (response.ok) {
          this.resetRegisterForm();
          this.showRegister = false;
          this.successMessage = "Account created successfully! Please sign in.";
          this.showSuccess = true;
        } else {
          this.error = data.error || "Registration failed";
          this.showError = true;
        }
      } catch (err) {
        this.error = "Connection error";
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    handleLogout() {
      this.isAuthenticated = false;
      this.currentUser = null;
      this.authToken = null;
      this.aiChatDrawer = false;
      localStorage.removeItem("support_token");
      localStorage.removeItem("support_user");
      this.successMessage = "Signed out successfully";
      this.showSuccess = true;
    },

    resetLoginForm() {
      this.email = "";
      this.password = "";
      this.showPassword = false;
      this.$refs.loginForm?.resetValidation();
    },

    resetRegisterForm() {
      this.name = "";
      this.registerEmail = "";
      this.registerPassword = "";
      this.confirmPassword = "";
      this.showPassword = false;
      this.$refs.registerForm?.resetValidation();
    },

    // Data Loading
    async loadUserData() {
      await this.fetchCategories();
    },

    async fetchCategories() {
      try {
        const response = await this.makeApiCall(`${this.apiUrl}/categories`);
        this.categories = response.filter((cat) => cat.is_active !== false);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    },

    // Ticket Methods
    async createTicket() {
      if (!this.$refs.createTicketForm.validate()) return;

      this.creatingTicket = true;
      try {
        const formData = new FormData();
        formData.append("title", this.newTicket.title);
        formData.append("description", this.newTicket.description);
        formData.append("category_id", this.newTicket.category_id);
        formData.append("priority", this.newTicket.priority);

        if (this.newTicket.attachments?.length) {
          this.newTicket.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }

        const response = await fetch(`${this.apiUrl}/tickets`, {
          method: "POST",
          headers: { Authorization: this.authToken },
          body: formData,
        });

        if (response.ok) {
          this.closeCreateTicketDialog();
          this.successMessage = "Ticket created successfully";
          this.showSuccess = true;
          // Emit event to refresh tickets if needed
          this.$root.$emit("ticket-created");
        }
      } catch (err) {
        this.error = "Failed to create ticket";
        this.showError = true;
      } finally {
        this.creatingTicket = false;
      }
    },

    closeCreateTicketDialog() {
      this.showCreateTicketDialog = false;
      this.newTicket = {
        title: "",
        description: "",
        category_id: null,
        priority: "medium",
        attachments: [],
      };
      this.$refs.createTicketForm?.resetValidation();
    },

    // API Helper
    async makeApiCall(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
          Authorization: this.authToken,
        },
      });

      if (response.status === 401) {
        this.handleSessionError();
        throw new Error("Session expired");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error ${response.status}`);
      }

      return await response.json();
    },

    handleSessionError() {
      this.handleLogout();
      this.error = "Your session has expired";
      this.showError = true;
    },

    // Utility Methods
    getUserInitials(name) {
      if (!name) return "U";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
  },
};
</script>

<style scoped>
/* Global App Styles */
.helpdesk-app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
  background: #fafafa;
}

/* Header Styles */
.app-header {
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.search-input >>> .v-input__slot {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.search-input >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.2) !important;
}

.user-menu-btn {
  text-transform: none !important;
  font-weight: 400 !important;
}

.user-menu {
  border-radius: 12px !important;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12) !important;
}

/* Auth Styles */
.auth-main {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.auth-card {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1) !important;
}

/* Navigation Styles */
.main-nav {
  border-right: 1px solid rgba(0, 0, 0, 0.05) !important;
}

/* Dashboard Styles */
.dashboard-view {
  min-height: 100vh;
}

.metric-card {
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 16px;
}

.chart-placeholder {
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed rgba(0, 0, 0, 0.1);
}

/* Form Styles */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
  >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.2) !important;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__slot {
  border-width: 2px !important;
}

.v-textarea >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

.v-file-input >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

/* Button Styles */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

.v-btn.v-btn--depressed {
  box-shadow: none !important;
}

.v-btn:not(.v-btn--text):not(.v-btn--outlined) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.v-btn:not(.v-btn--text):not(.v-btn--outlined):hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
}

/* Card Styles */
.v-card {
  border-radius: 12px !important;
}

.v-card.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

/* Dialog Styles */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

/* Snackbar Styles */
.v-snackbar >>> .v-snack__wrapper {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .search-input {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .page-header {
    margin-bottom: 24px;
  }
  .metric-card {
    margin-bottom: 16px;
  }
  .chart-placeholder {
    height: 150px;
  }
}
</style>
